import { useId, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { useLoadingZoneStore, createZoneStore, type ZoneStore } from './LoadingZoneContext';

export interface LoadingZoneRegistration {
  /** True when another zone owns the screen's one spinner: this zone draws its overlay without it. */
  hideSpinner: boolean;
  /** True when this zone draws the screen's spinner (and its Try again). */
  isLeader: boolean;
  /** Zones waiting right now. */
  zoneCount: number;
  /** When the current waiting episode began (ms); null when nothing waits. */
  startedAt: number | null;
  /** Some waiting zone has an `onRetry`. */
  hasRetry: boolean;
  /** Refetch every waiting zone and restart the Try again clock. */
  retryAll: () => void;
}

export interface LoadingZoneRegistrationOptions {
  /** Win the screen's one spinner over earlier-registered zones. */
  primary?: boolean;
  /** Refetch exactly this region's queries (Try again). */
  onRetry?: () => void;
}

/**
 * Auto-register a loading zone.
 *
 * When `active` is true, registers a zone ID. When `active` becomes false
 * (or the component unmounts), unregisters. With several zones waiting,
 * only the leader (`primary`, else a zone with a retry, else the earliest registered) draws a spinner
 * (#1561 W5); every other zone draws its own overlay without one.
 *
 * Outside `LoadingZoneProvider` (e.g. login pages) the zone is alone in a
 * store of its own, so it is always the leader.
 */
export function useLoadingZoneRegistration(
  active = true,
  options: LoadingZoneRegistrationOptions = {},
): LoadingZoneRegistration {
  const id = useId();
  const ctxStore = useLoadingZoneStore();
  const localRef = useRef<ZoneStore | null>(null);
  if (!ctxStore && !localRef.current) localRef.current = createZoneStore();
  const store = (ctxStore ?? localRef.current) as ZoneStore;

  const primary = options.primary === true;
  const hasRetry = options.onRetry !== undefined;
  const onRetryRef = useRef(options.onRetry);
  onRetryRef.current = options.onRetry;

  const snap = useSyncExternalStore(store.subscribe, store.getSnapshot);

  useLayoutEffect(() => {
    if (!active) return;
    store.register(id, { primary, hasRetry, retry: () => onRetryRef.current?.() });
    return () => store.unregister(id);
  }, [store, id, active, primary, hasRetry]);

  // Before the effect has registered us (first render) we are alone or not yet
  // known: treat "no leader yet" as leader so the first paint shows a spinner.
  const isLeader = snap.leaderId === null || snap.leaderId === id;
  return {
    hideSpinner: !isLeader,
    isLeader,
    zoneCount: snap.count,
    startedAt: snap.startedAt,
    hasRetry: snap.hasRetry,
    retryAll: store.retryAll,
  };
}
